<template>
  <modal width="560px" :title="t('Payment Information')" @close="close">
    <div class="wrapper">
      <div class="wrapper-container">
        <ui-icon size="42" :color="$colors.green" icon="fas fa-wallet" />
        <ui-radio-group
          class="mt-8 mb-8"
          v-model="form.referralPaymentAccountType"
          :label="t('Payment System')"
        >
          <div class="d-flex mt-8">
            <ui-radio label="PayPal" value="paypal" />
            <ui-radio class="ml-16" label="Webmoney" value="webmoney" />
          </div>
        </ui-radio-group>
        <ui-input
          class="step-z"
          v-model="form.referralPaymentAccount"
          :label="t('Account')"
          :placeholder="t('Account')"
        />
      </div>
    </div>

    <template #footer>
      <section class="justify-end xs12 d-flex">
        <ui-button
          :text="t('Cancel')"
          icon="fas fa-times"
          :disabled="submitting"
          @click="close"
        />

        <ui-button
          :text="t('Save')"
          icon="fas fa-check"
          button-style="accent"
          :loading="submitting"
          @click="submit"
        />
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { reactive, ref, watch } from 'vue';
import { useStore } from '@/store';
import { pick } from 'lodash';
import { useColors, useI18n } from '@/composables';
import { putUserApiRequest } from '@/services/api/user-api';
import type { User } from '@/services/models';
import { $emitter } from '@/services/emitter';

const emit = defineEmits<{ close: [] }>();

const store = useStore();
const { t } = useI18n();
const $colors = useColors();

const submitting = ref(false);

const form = reactive({
  referralPaymentAccountType: '',
  referralPaymentAccount: '',
});

const close = () => {
  emit('close');
};

const submit = async () => {
  submitting.value = true;
  try {
    const payload = {
      referralPaymentAccountType: form.referralPaymentAccountType,
      referralPaymentAccount: form.referralPaymentAccount,
    } satisfies Partial<User>;
    const res = await putUserApiRequest(payload);
    store.mergeUser(res);
    close();
    $emitter.emit('toast-success', t('Payment Details have been changed'));
  } finally {
    submitting.value = false;
  }
};

const fillForm = (source: User) => {
  const picked = pick(source, [
    'referralPaymentAccountType',
    'referralPaymentAccount',
  ]) as Partial<User>;
  form.referralPaymentAccountType = (picked.referralPaymentAccountType as string) ?? '';
  form.referralPaymentAccount = (picked.referralPaymentAccount as string) ?? '';
};

watch(
  () => store.user,
  user => {
    fillForm(user as User);
  },
  { deep: true, immediate: true },
);
</script>

<style lang="scss" scoped>
.wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}
.wrapper-container {
  width: 300px;
  padding: 24px;
}
</style>
